import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { FaChevronLeft, FaInfoCircle, FaTable } from 'react-icons/fa';
import { FaBrain } from 'react-icons/fa6';
import SyncedControlBar from './SyncedControlBar';
import CompareRunPane from './CompareRunPane';
import { computeCompareSuspensionBundle } from '../utils/compareSuspensionBundle';
import { buildCompareSuspensionInsightPayload } from '../utils/compareSuspensionOpenAi';
import { fetchOpenAiTuningInsight, resolveOpenAiApiKey, formatOpenAiInsightHtml } from '../utils/openaiTuningInsight';
import { getTuningGuideConfig, getTelemetryTimeBounds } from '../utils/jackSettings';
import { enrichRunWithTelemetryData } from '../utils/enrichRunWithTelemetryData';
import { canViewRunClient } from '../utils/runAccess';
import { toneSecondVsBenchmark } from '../utils/compareSummaryTableTone';
import './Compare.css';

const RIDE1_COLOR = '#6a5cb8';
const RIDE2_COLOR = '#2196F3';

const getDuration = (ride) => {
  if (ride?.duration && Number.isFinite(ride.duration)) return ride.duration;
  const td = ride?.telemetryData;
  if (Array.isArray(td) && td.length > 0) {
    const maxTime = Math.max(...td.map((d) => d.time ?? 0));
    return maxTime;
  }
  return 0;
};

function fmtNum(v, digits = 1) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(digits);
}

function hasTableData(snap) {
  if (!snap || typeof snap !== 'object') return false;
  if (snap.jackOk) return true;
  return [snap.balanceRatio, snap.roughness, snap.dynSagFrontPct, snap.travelFrontPct].some(
    (x) => x != null && Number.isFinite(Number(x)),
  );
}

function CompareNameLabelLine({ fullTitle, children, showMobileDetails = true }) {
  return (
    <div className="compare-name-label">
      <div
        className="compare-name-line"
        title={fullTitle}
      >
        {children}
      </div>
      {showMobileDetails && (
        <details className="compare-name-mobile-details">
          <summary
            className="compare-name-mobile-details-summary"
            aria-label="Show full name"
          >
            <FaInfoCircle aria-hidden />
          </summary>
          <p className="compare-name-mobile-details-body">{fullTitle}</p>
        </details>
      )}
    </div>
  );
}

function CompareSuspensionTable({ name1, name2, snap1, snap2, tuningConfig = {} }) {
  const rows = useMemo(
    () => [
      { id: 'balance', label: 'Balance (F/R)', r1: snap1?.balanceRatio, r2: snap2?.balanceRatio, digits: 2 },
      { id: 'sagF', label: 'Dynamic sag front (%)', r1: snap1?.dynSagFrontPct, r2: snap2?.dynSagFrontPct },
      { id: 'sagR', label: 'Dynamic sag rear (%)', r1: snap1?.dynSagRearPct, r2: snap2?.dynSagRearPct },
      { id: 'travelF', label: 'Max travel front (%)', r1: snap1?.travelFrontPct, r2: snap2?.travelFrontPct },
      { id: 'travelR', label: 'Max travel rear (%)', r1: snap1?.travelRearPct, r2: snap2?.travelRearPct },
      { id: 'boF', label: 'Bottom-outs front', r1: snap1?.bottomF, r2: snap2?.bottomF, int: true },
      { id: 'boR', label: 'Bottom-outs rear', r1: snap1?.bottomR, r2: snap2?.bottomR, int: true },
      { id: 'toF', label: 'Top-outs front', r1: snap1?.topF, r2: snap2?.topF, int: true },
      { id: 'toR', label: 'Top-outs rear', r1: snap1?.topR, r2: snap2?.topR, int: true },
      { id: 'packingPct', label: 'Packing index (%)', r1: snap1?.packingIndex, r2: snap2?.packingIndex, int: true },
      { id: 'packingRating', label: 'Packing rating', r1: snap1?.packingRating, r2: snap2?.packingRating, str: true },
      { id: 'roughness', label: 'Roughness index', r1: snap1?.roughness, r2: snap2?.roughness, digits: 2 },
    ],
    [snap1, snap2],
  );

  return (
    <div className="compare-summary-table-wrap">
      <table className="compare-summary-table">
        <colgroup>
          <col className="compare-summary-col--metric" />
          <col className="compare-summary-col--ref" />
          <col className="compare-summary-col--cmp" />
        </colgroup>
        <thead>
          <tr>
            <th className="compare-th-metric" scope="col">
              Metric
            </th>
            <th className="compare-th-name" scope="col">
              <CompareNameLabelLine fullTitle={`${name1} (reference)`}>
                <span className="compare-th-name-ref">(ref) </span>
                {name1}
              </CompareNameLabelLine>
            </th>
            <th
              className="compare-th-name"
              scope="col"
            >
              <CompareNameLabelLine
                fullTitle={`${name2} — Colored vs first column: green = more desirable, red = less, default = similar`}
              >
                {name2}
              </CompareNameLabelLine>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const tone = toneSecondVsBenchmark(row.id, row.r1, row.r2, tuningConfig);
            const toneClass =
              tone === 'better'
                ? 'compare-summary-value--better'
                : tone === 'worse'
                  ? 'compare-summary-value--worse'
                  : '';
            const fmt = (v, int, str) => {
              if (str) return v != null && v !== '' ? String(v) : '—';
              if (v == null || (typeof v === 'string' && v === '')) return '—';
              if (int) {
                const n0 = Number(v);
                if (!Number.isFinite(n0)) return '—';
                return String(Math.round(n0));
              }
              return fmtNum(v, row.digits != null ? row.digits : 1);
            };
            return (
              <tr key={row.id}>
                <td>{row.label}</td>
                <td className="compare-summary-value-ref">{fmt(row.r1, row.int, row.str)}</td>
                <td className={tone ? `${toneClass} compare-summary-value-compare` : 'compare-summary-value-compare'}>
                  {fmt(row.r2, row.int, row.str)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function buildControlBarTrim(ride) {
  if (!ride) return null;
  const { minT, maxT } = getTelemetryTimeBounds(ride?.telemetryData);
  if (!(maxT > minT)) return null;
  return {
    minTime: minT,
    maxTime: maxT,
    startTime: ride.analysisTrim?.startTime != null ? ride.analysisTrim.startTime : minT,
    endTime: ride.analysisTrim?.endTime != null ? ride.analysisTrim.endTime : maxT,
  };
}

const Compare = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = auth.currentUser;

  const rideIds = useMemo(() => {
    const raw = location.state?.rideIds;
    return Array.isArray(raw) ? raw : [];
  }, [location.state?.rideIds]);

  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [summarySubTab, setSummarySubTab] = useState('table');
  const [compareAiText, setCompareAiText] = useState('');
  const [compareAiLoading, setCompareAiLoading] = useState(false);
  const [compareAiError, setCompareAiError] = useState(null);
  /** 'summary' | 0 | 1 — which run is active for the large layout */
  const [mainTab, setMainTab] = useState('summary');
  const [isCompareWideLayout, setIsCompareWideLayout] = useState(
    () => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 1280px)').matches : false),
  );
  /** 0|1 — which run drives trim / timeline when wide 3-col layout is shown */
  const [wideSyncRun, setWideSyncRun] = useState(0);
  const [trainingZones, setTrainingZones] = useState(null);

  const handleRideDocumentPatch = useCallback((rideId, patch) => {
    if (!rideId || !patch) return;
    setRides((prev) =>
      prev.map((r) => (r.id === rideId ? { ...r, ...patch } : r)),
    );
  }, []);

  const bundle1 = useMemo(() => {
    const ride = rides[0];
    const td = ride?.telemetryData;
    if (!ride || !Array.isArray(td) || td.length === 0) return null;
    return computeCompareSuspensionBundle(td, ride);
  }, [rides]);

  const bundle2 = useMemo(() => {
    const ride = rides[1];
    const td = ride?.telemetryData;
    if (!ride || !Array.isArray(td) || td.length === 0) return null;
    return computeCompareSuspensionBundle(td, ride);
  }, [rides]);

  const compareTableTuningConfig = useMemo(() => getTuningGuideConfig(), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1280px)');
    const onChange = () => setIsCompareWideLayout(mq.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
    } else {
      mq.addListener(onChange);
    }
    return () => {
      if (typeof mq.removeEventListener === 'function') {
        mq.removeEventListener('change', onChange);
      } else {
        mq.removeListener(onChange);
      }
    };
  }, []);

  useEffect(() => {
    if (!isCompareWideLayout) return;
    if (mainTab === 0) setWideSyncRun(0);
    else if (mainTab === 1) setWideSyncRun(1);
    else setWideSyncRun(0);
  }, [isCompareWideLayout, mainTab]);

  useEffect(() => {
    if (!user) {
      setTrainingZones(null);
      return;
    }
    let isCancelled = false;
    (async () => {
      try {
        const sport = (rides[0] && rides[0].sport) || 'cycling';
        const zonesQuery = query(
          collection(db, 'trainingZones'),
          where('userId', '==', user.uid),
          where('sport', '==', sport),
        );
        const snapshot = await getDocs(zonesQuery);
        if (isCancelled) return;
        if (!snapshot.empty) setTrainingZones(snapshot.docs[0].data());
        else setTrainingZones(null);
      } catch (e) {
        if (!isCancelled) setTrainingZones(null);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [user, rides]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    const ids = Array.isArray(rideIds) ? rideIds.slice(0, 2) : [];
    if (ids.length < 2) {
      setLoading(false);
      setRides([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (auth.currentUser) {
          try {
            await auth.currentUser.getIdToken(false);
          } catch (_e) {
            /* ignore */
          }
        }
        const [r1, r2] = await Promise.all([getDoc(doc(db, 'runs', ids[0])), getDoc(doc(db, 'runs', ids[1]))]);
        if (cancelled) return;
        if (!r1.exists() || !r2.exists()) {
          if (!cancelled) setRides([]);
          return;
        }
        let d1 = { id: r1.id, ...r1.data() };
        let d2 = { id: r2.id, ...r2.data() };
        if (!canViewRunClient(user, d1) || !canViewRunClient(user, d2)) {
          alert('You do not have permission to view one of these runs.');
          navigate('/my-rides');
          return;
        }
        await enrichRunWithTelemetryData(d1.id, d1, { user, navTelemetry: d1.telemetryData });
        await enrichRunWithTelemetryData(d2.id, d2, { user, navTelemetry: d2.telemetryData });
        if (cancelled) return;
        setRides([d1, d2]);
        setDuration(Math.max(getDuration(d1), getDuration(d2)));
      } catch (err) {
        console.error('Error loading rides for compare:', err);
        if (!cancelled) setRides([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, navigate, rideIds]);

  const handleSeek = useCallback((time) => {
    setCurrentTime(time);
  }, []);

  const handleTimeUpdate = useCallback((newTime) => {
    setCurrentTime(newTime);
  }, []);

  const handleDurationChange = useCallback((newDuration) => {
    setDuration((prev) => Math.max(prev, newDuration));
  }, []);

  const handlePlayPause = useCallback(() => {
    setIsPlaying((p) => !p);
  }, []);

  const handleGenerateCompareAi = useCallback(async () => {
    if (!bundle1 || !bundle2) return;
    setCompareAiError(null);
    setCompareAiLoading(true);
    try {
      const ranges = getTuningGuideConfig();
      const apiKey = resolveOpenAiApiKey(ranges);
      const n1 = rides[0]?.runName || rides[0]?.name || 'Run 1';
      const n2 = rides[1]?.runName || rides[1]?.name || 'Run 2';
      const payload = buildCompareSuspensionInsightPayload(
        n1,
        bundle1.openAiSlice,
        n2,
        bundle2.openAiSlice,
      );
      const text = await fetchOpenAiTuningInsight(apiKey, payload);
      setCompareAiText(text);
    } catch (e) {
      setCompareAiError(e?.message || String(e));
    } finally {
      setCompareAiLoading(false);
    }
  }, [bundle1, bundle2, rides]);

  const compareAiHtml = useMemo(() => formatOpenAiInsightHtml(compareAiText), [compareAiText]);

  if (!user) return null;

  if (loading) {
    return (
      <div className="compare-container">
        <div className="compare-header">
          <h1>Compare Runs</h1>
          <p className="subtitle">Loading runs…</p>
        </div>
        <div className="loading-spinner" style={{ minHeight: 200 }}>
          Loading…
        </div>
      </div>
    );
  }

  const ids = Array.isArray(rideIds) ? rideIds.slice(0, 2) : [];
  if (ids.length < 2 || rides.length < 2) {
    return (
      <div className="compare-container">
        <div className="compare-header">
          <button
            type="button"
            className="compare-back-button"
            onClick={() => navigate('/my-rides')}
          >
            <FaChevronLeft /> Back
          </button>
          <h1>Compare Runs</h1>
          <p className="subtitle">Select at least 2 runs from My Runs, then click Compare.</p>
        </div>
        <div className="empty-comparison">
          <h3>Select 2 runs to compare</h3>
          <p>Go to My Rides, use Select, pick 2 runs, then Compare.</p>
          <button type="button" className="compare-rides-button" onClick={() => navigate('/my-rides')}>
            Go to My Rides
          </button>
        </div>
      </div>
    );
  }

  const [ride1, ride2] = rides;
  const name1 = ride1?.runName || ride1?.name || 'Run 1';
  const name2 = ride2?.runName || ride2?.name || 'Run 2';
  const duration1 = getDuration(ride1);
  const duration2 = getDuration(ride2);
  const dataOffset1 = ride1?.dataOffset ?? 0;
  const dataOffset2 = ride2?.dataOffset ?? 0;
  const hasTelemetry1 = ride1?.telemetryData?.length > 0;
  const hasTelemetry2 = ride2?.telemetryData?.length > 0;

  const snap1 = bundle1?.summarySnapshot;
  const snap2 = bundle2?.summarySnapshot;

  const timelineTracks = [
    { duration: duration1, color: RIDE1_COLOR },
    { duration: duration2, color: RIDE2_COLOR },
  ];

  const activeDataOffset = isCompareWideLayout
    ? wideSyncRun === 0
      ? dataOffset1
      : dataOffset2
    : mainTab === 0
      ? dataOffset1
      : mainTab === 1
        ? dataOffset2
        : 0;
  const analysisTrim = isCompareWideLayout
    ? wideSyncRun === 0 && hasTelemetry1
      ? buildControlBarTrim(ride1)
      : wideSyncRun === 1 && hasTelemetry2
        ? buildControlBarTrim(ride2)
        : null
    : mainTab === 0 && hasTelemetry1
      ? buildControlBarTrim(ride1)
      : mainTab === 1 && hasTelemetry2
        ? buildControlBarTrim(ride2)
        : null;

  const summaryPanelInner = (
    <div className="compare-summary-panel compare-summary-panel--structured">
      <h3 className="compare-summary-title">Summary</h3>
      <div className="compare-summary-subtabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`compare-summary-subtab ${summarySubTab === 'table' ? 'active' : ''}`}
          onClick={() => setSummarySubTab('table')}
        >
          <FaTable aria-hidden /> Table
        </button>
        <button
          type="button"
          className={`compare-summary-subtab ${summarySubTab === 'ai' ? 'active' : ''}`}
          onClick={() => setSummarySubTab('ai')}
        >
          <FaBrain aria-hidden /> AI insight
        </button>
      </div>
      {summarySubTab === 'table' && (
        <div role="tabpanel" className="compare-summary-subpanel">
          {!hasTableData(snap1) && !hasTableData(snap2) ? (
            <p className="compare-summary-muted">
              No suspension metrics for either run. If telemetry is stored in the cloud or on this device, ensure both
              runs finished loading, then refresh.
            </p>
          ) : (
            <CompareSuspensionTable
              name1={name1}
              name2={name2}
              snap1={snap1}
              snap2={snap2}
              tuningConfig={compareTableTuningConfig}
            />
          )}
        </div>
      )}
      {summarySubTab === 'ai' && (
        <div role="tabpanel" className="compare-summary-subpanel compare-summary-ai">
          <button
            type="button"
            className="compare-ai-generate-btn"
            onClick={handleGenerateCompareAi}
            disabled={compareAiLoading || !bundle1 || !bundle2}
          >
            {compareAiLoading ? 'Generating…' : compareAiText ? 'Regenerate' : 'Generate insight'}
          </button>
          {compareAiError && <p className="compare-summary-error">{compareAiError}</p>}
          {!compareAiText && !compareAiLoading && !compareAiError && (
            <p className="compare-summary-muted">Generate an AI summary of differences between these runs.</p>
          )}
          {compareAiText && !compareAiLoading && (
            <div className="compare-ai-insight-html" dangerouslySetInnerHTML={{ __html: compareAiHtml }} />
          )}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`compare-container compare-view-mode compare-ride-style${isCompareWideLayout ? ' compare-ride-style--wide' : ''}`}
    >
      <div className="compare-header">
        <div className="compare-header-row">
          <button type="button" className="compare-back-button" onClick={() => navigate('/my-rides')}>
            <FaChevronLeft /> Back
          </button>
          <h1>
            Compare: {name1} vs {name2}
          </h1>
        </div>
      </div>

      {!isCompareWideLayout && (
        <>
          <div className="compare-primary-tabs" role="tablist">
            <button
              type="button"
              className={`compare-primary-tab ${mainTab === 'summary' ? 'active' : ''}`}
              onClick={() => setMainTab('summary')}
            >
              Summary
            </button>
            <button
              type="button"
              className={`compare-primary-tab ${mainTab === 0 ? 'active' : ''}`}
              onClick={() => setMainTab(0)}
            >
              {name1}
            </button>
            <button
              type="button"
              className={`compare-primary-tab ${mainTab === 1 ? 'active' : ''}`}
              onClick={() => setMainTab(1)}
            >
              {name2}
            </button>
          </div>

          <div className="compare-primary-tab-body">
            {mainTab === 'summary' && <div className="compare-tab-pane compare-tab-pane--summary">{summaryPanelInner}</div>}

            {mainTab === 0 && (
              <div className="compare-tab-pane">
                <CompareRunPane
                  key={ride1.id}
                  ride={ride1}
                  trainingZones={trainingZones}
                  currentTime={currentTime}
                  isPlaying={isPlaying}
                  onSeek={handleSeek}
                  onTimeUpdate={handleTimeUpdate}
                  onDurationChange={handleDurationChange}
                  onRideDocumentPatch={handleRideDocumentPatch}
                />
              </div>
            )}

            {mainTab === 1 && (
              <div className="compare-tab-pane">
                <CompareRunPane
                  key={ride2.id}
                  ride={ride2}
                  trainingZones={trainingZones}
                  currentTime={currentTime}
                  isPlaying={isPlaying}
                  onSeek={handleSeek}
                  onTimeUpdate={handleTimeUpdate}
                  onDurationChange={handleDurationChange}
                  onRideDocumentPatch={handleRideDocumentPatch}
                />
              </div>
            )}
          </div>
        </>
      )}

      {isCompareWideLayout && (
        <div className="compare-wide-grid" aria-label="Summary and runs side by side">
          <div className="compare-wide-col compare-wide-col--summary">{summaryPanelInner}</div>
          <div
            className={`compare-wide-col compare-wide-col--ride ${wideSyncRun === 0 ? 'is-sync-active' : ''}`}
            role="presentation"
          >
            <button
              type="button"
              className={`compare-wide-run-head${wideSyncRun === 0 ? ' is-active' : ''}`}
              onClick={() => setWideSyncRun(0)}
              aria-pressed={wideSyncRun === 0}
            >
              <span className="compare-wide-run-head-eyebrow" style={{ color: RIDE1_COLOR }}>
                Run 1
              </span>
              <CompareNameLabelLine showMobileDetails={false} fullTitle={`${name1} (reference)`}>
                <span className="compare-th-name-ref">(ref) </span>
                {name1}
              </CompareNameLabelLine>
            </button>
            <div className="compare-wide-col-scroll">
              <CompareRunPane
                key={ride1.id}
                ride={ride1}
                trainingZones={trainingZones}
                currentTime={currentTime}
                isPlaying={isPlaying}
                onSeek={handleSeek}
                onTimeUpdate={handleTimeUpdate}
                onDurationChange={handleDurationChange}
                onRideDocumentPatch={handleRideDocumentPatch}
              />
            </div>
          </div>
          <div
            className={`compare-wide-col compare-wide-col--ride ${wideSyncRun === 1 ? 'is-sync-active' : ''}`}
            role="presentation"
          >
            <button
              type="button"
              className={`compare-wide-run-head${wideSyncRun === 1 ? ' is-active' : ''}`}
              onClick={() => setWideSyncRun(1)}
              aria-pressed={wideSyncRun === 1}
            >
              <span className="compare-wide-run-head-eyebrow" style={{ color: RIDE2_COLOR }}>
                Run 2
              </span>
              <CompareNameLabelLine showMobileDetails={false} fullTitle={name2}>
                {name2}
              </CompareNameLabelLine>
            </button>
            <div className="compare-wide-col-scroll">
              <CompareRunPane
                key={ride2.id}
                ride={ride2}
                trainingZones={trainingZones}
                currentTime={currentTime}
                isPlaying={isPlaying}
                onSeek={handleSeek}
                onTimeUpdate={handleTimeUpdate}
                onDurationChange={handleDurationChange}
                onRideDocumentPatch={handleRideDocumentPatch}
              />
            </div>
          </div>
        </div>
      )}

      <div className="compare-control-bar-anchor">
        <SyncedControlBar
          currentTime={currentTime}
          duration={duration}
          isPlaying={isPlaying}
          onPlayPause={handlePlayPause}
          onSeek={handleSeek}
          dataOffset={activeDataOffset}
          analysisTrim={analysisTrim}
          analysisTrimDisabled
          onAnalysisTrimChange={undefined}
          comments={[]}
          tracks={timelineTracks}
        />
      </div>
    </div>
  );
};

export default Compare;
